import { Injectable, OnDestroy } from '@angular/core';

import { Subscription ,  Observable, of } from 'rxjs';
import { filter } from 'rxjs/operators';

import { QuickSearchOutletsContactsParam } from './model/quick-search-outlets-contacts-param';
import { QuickSearchOutletsContactsResponse } from './model/quick-search-outlets-contacts-response';
import { HttpVerbsEnum } from '../shared/enums/http-verbs.enum';
import { JournodexGeographyEntityApiModel } from './model/journodex-geography-entity-api.model';
import { QuickSearchGeographyParam } from './model/quick-search-geography-param';
import { LookupByGeoEntitiesParam } from './model/lookup-by-geo-entities-param';
import { LookupByGeoEntitiesResponse } from './model/lookup-by-geo-entities-response';
import { CountryApiModel } from './model/country-api.model';
import { GetChildRegionModel } from './model/get-child-region.param';
import { JournodexGetImmediateChildRegionModel } from './model/journodex-get-immediate-child-region.model';
import { GeographyTypesEnum } from './enum/geography-types.enum';
import { JournodexSubjectApiModel } from './model/journodex-subject-api.model';
import { QuickSearchSubjectsRequestModel } from './model/quick-search-subjects-request.model';
import { LocalizedEnumApiModel } from './model/localized-enum-api.model';
import { JobRoleApiModel } from './model/job-role-api.model';
import { JournodexEnumTypes } from './enum/journodex-enum-types.enum';
import { MediaMarketApi } from './model/media-market.api.model';
import { CurrentUserStateService } from '../../Core/Users/services/current-user-state.service';
import { JournodexQuickSearchEntityApiModel } from './model/journodex-quick-search-entity-api.model';
import { EntityChangeRequestApiModel } from './model/entity-change-request-api.model';
import { NeptuneApi } from '../genericApi/neptune.api';

@Injectable()
export class JournodexApiService implements OnDestroy {

    private locale: string = 'en-US';
    private subscriptions: Subscription[] = [];

    constructor(
        private neptuneApi: NeptuneApi,
        private currentUserStateService: CurrentUserStateService,
    ) {
        this.subscriptions.push(
            this.currentUserStateService.getDataReferenceLanguageTag().pipe(
                filter(x => !!x)
            ).subscribe(languageTag => {
                this.locale = languageTag;
            })
        );
    }

    /**
     * Perform a search call through Journodex API for outlets matching provided search query
     *
     * @param queryString
     */
    performQuickSearchOutlets(queryString: string): Observable<QuickSearchOutletsContactsResponse[]> {
        const body: QuickSearchOutletsContactsParam = {
            query: queryString,
            numberOfResults: 5,
            groupResults: true,
        };

        return this.neptuneApi.api<QuickSearchOutletsContactsResponse[]>(
            '/outlets/public/quicksearch',
            body,
            HttpVerbsEnum.POST
        );
    }

    /**
     * Perform a search call through Journodex API for contacts matching provided search query
     *
     * @param queryString
     */
    performQuickSearchContacts(queryString: string): Observable<QuickSearchOutletsContactsResponse[]> {
        const body: QuickSearchOutletsContactsParam = {
            query: queryString,
            numberOfResults: 5,
            groupResults: true,
        };

        return this.neptuneApi.api<QuickSearchOutletsContactsResponse[]>(
            '/contacts/quicksearchuuid',
            body,
            HttpVerbsEnum.POST
        );
    }

    /**
     * Perform a search call through Journodex API for contacts matching provided search query
     *
     * @param queryString
     */
    performQuickSearchSubjects(
        queryString: string,
        count: number = 5,
        groupResults: boolean = true
    ): Observable<JournodexSubjectApiModel[]> {
        const body: QuickSearchSubjectsRequestModel = {
            query: queryString,
            numberOfResults: count,
            groupResults: groupResults,
        };

        return this.neptuneApi.api<JournodexSubjectApiModel[]>(
            `/journodex-subjects/quicksearch?locale=${this.locale}`,
            body,
            HttpVerbsEnum.POST
        );
    }

    /**
     * Perform a search call through Journodex API for subjects relating to provided search query,
     * based on mappings by research team
     *
     * @param queryString
     */
    performQuickSearchRelatedSubjects(
        queryString: string,
        count: number = 50,
        groupResults: boolean = true
    ): Observable<JournodexSubjectApiModel[]> {
        const body: QuickSearchSubjectsRequestModel = {
            query: queryString,
            numberOfResults: count,
            groupResults: groupResults,
        };

        return this.neptuneApi.api<JournodexSubjectApiModel[]>(
            `/journodex-subjects/quicksearchrelatedsubjects?locale=${this.locale}`,
            body,
            HttpVerbsEnum.POST
        );
    }

    getAllSubjects(): Observable<JournodexSubjectApiModel[]> {
        return this.neptuneApi.api<JournodexSubjectApiModel[]>(
            `/journodex-subjects/?locale=${this.locale}`,
            null,
            HttpVerbsEnum.GET
        );
    }

    /**
     * Perform a search call through Journosdex API for geographic/location/region information
     *
     * @param queryString
     */
    performQuickSearchGeography(
        queryString: string,
        locale: string,
        applyAccountRestrictions: boolean = false
    ): Observable<JournodexGeographyEntityApiModel[]> {

        const body: QuickSearchGeographyParam = {
            query: queryString,
            numberOfResults: 25,
            groupResults: true,
        };

        return this.neptuneApi.api<JournodexGeographyEntityApiModel[]>(
            '/geographies/quicksearch?locale=' + locale,
            body,
            HttpVerbsEnum.POST,
            {
                applyAccountRestrictions: applyAccountRestrictions
            }
        );
    }

    /**
     * Perform a search call through Journosdex API for contact and outlet information
     *
     * @param queryString
     */
    contactOutletQuickSearch(queryString: string): Observable<JournodexQuickSearchEntityApiModel[]> {
        const body = {
            query: queryString
        };

        return this.neptuneApi.api<JournodexQuickSearchEntityApiModel[]>(
            '/journodex-searches/quicksearchuuid',
            body,
            HttpVerbsEnum.POST
        );
    }

    /**
     * Fetch geographic data based upon the entity specific id value for an entity
     *
     * This does not lookup by the unique Geographic Id but the id value that is unique within
     * an entity space.
     */
    performLookupByGeoEntities(
        entityList: number[],
        entityType: GeographyTypesEnum,
        locale: string = 'en-US'
    ): Observable<LookupByGeoEntitiesResponse[]> {
        const lookupBody: LookupByGeoEntitiesParam[] = [];
        for (const entity of entityList) {
            lookupBody.push({
                geoEntityId: entity,
                geographyType: entityType
            });
        }

        return this.neptuneApi.api<LookupByGeoEntitiesResponse[]>(
            `/geographies/lookupByGeoEntities?locale=${locale}`,
            lookupBody,
            HttpVerbsEnum.POST
        );
    }

    /**
     * Gets a list of all of the countries, including their country codes
     */
    getAllCountries(): Observable<CountryApiModel[]> {
        const slug: string = `/geographies/getAllCountries?locale=${this.locale}`;

        return this.neptuneApi.api<CountryApiModel[]>(
            slug,
            null,
            HttpVerbsEnum.GET
        );
    }

    /**
     * Fetches Geographic data of a specified type given its parent element
     *
     * @param requestParams specifies parent element and type of result expected
     *
     * @returns Observable<JournodexGeographyEntityApiModel>
     */
    getChildRegions(requestParams: GetChildRegionModel): Observable<JournodexGeographyEntityApiModel[]> {
        const slug: string = `/geographies/getChildRegions?locale=${this.locale}`;

        return this.neptuneApi.api<any[]>(
            slug,
            null,
            HttpVerbsEnum.GET,
            requestParams
        );
    }

    /**
     * Fetch geographic data that is a direct child of the parameters provided
     */
    getImmediateChildRegions(requestParams: JournodexGetImmediateChildRegionModel): Observable<JournodexGeographyEntityApiModel[]> {
        const slug: string = `/geographies/immediateChildrenRegions?locale=${this.locale}`;

        return this.neptuneApi.api<any[]>(
            slug,
            null,
            HttpVerbsEnum.GET,
            requestParams
        );
    }

    getEnum(type: JournodexEnumTypes): Observable<LocalizedEnumApiModel[]> {
        const slug: string = `/journodex-enums/${type}?locale=${this.locale}`;

        return this.neptuneApi.api<LocalizedEnumApiModel[]>(slug, null, HttpVerbsEnum.GET);
    }

    getAllJobRoles(): Observable<JobRoleApiModel[]> {
        const slug: string = `/journodex-enums/jobrole?locale=${this.locale}`;

        return this.neptuneApi.api<JobRoleApiModel[]>(slug, null, HttpVerbsEnum.GET);
    }

    getAllMediaMarkets(): Observable<MediaMarketApi[]> {
        const slug: string = `/journodex-enums/mediamarket?locale=${this.locale}`;

        return this.neptuneApi.api<MediaMarketApi[]>(slug, null, HttpVerbsEnum.GET);
    }

    batchGetGeos (ids: number[]): Observable<JournodexGeographyEntityApiModel[]> {
        if (ids.length < 1) {
            return of([]);
        }

        return this.neptuneApi.api<JournodexGeographyEntityApiModel[]>(
            `/geographies/batchGet?locale=${this.locale}`,
            {ids: ids},
            HttpVerbsEnum.POST
        );
    }

    entityChangeRequest(changeRequest: EntityChangeRequestApiModel): Observable<void> {
        const slug: string = '/research/request';

        return this.neptuneApi.api<void>(
            slug,
            changeRequest,
            HttpVerbsEnum.POST
        );
    }

    getOutletAvatar(ids: string[]) {
        const slug: string = '/outlets/batchGet/avatars';

        return this.neptuneApi.api<any>(
            slug,
            {ids},
            HttpVerbsEnum.POST
        );
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
